import { Trans, t } from "@web-monorepo/i18n/macro";
import { useI18nLocale } from "@web-monorepo/i18n/hooks";
import * as React from "react";
import { ErrorBannerValue, ErrorBannerValues } from "./errorBannerTypes";
import { registerErrorBannerSetter, type ErrorBannerStateProps } from "./errorBannerApi";
import { ErrorPage } from "./ErrorPage";
import { DDSBodyText, DDSExternalLink, DDSScreenBanner } from "@web-monorepo/dds";
import { useWatch } from "@web-monorepo/hooks";
import { AppBrokenSource, captureAppBrokenMessage } from "@web-monorepo/telemetry";
import { TranslationSafeText } from "@web-monorepo/translation-safe-dom";
import styles from "./ErrorBanner.module.css";

declare const global: {
  errorBannerAllowed?: boolean;
};

const GoHome = ({ href }: { href: string }) => (
  <a
    onClick={() => {
      window.location.assign(href);
      window.location.reload();
    }}
    href={href}
    data-name="error_banner:go_home_a"
  >
    <span className={styles.link}>{t`refresh the page`}</span>
  </a>
);

interface ErrorBannerProps {
  errorType: ErrorBannerValue;
  href?: string;
  onClose?: () => void;
  alreadyReported?: boolean;
  route?: string;
}

// Every rendered surface owns its own `app_broken` reporting: ErrorPage decides
// internally (client-side breakage reports; HTTP-status pages don't), and the two
// banner variants below use this hook — so there is no separate list of "which
// branches report" to keep in sync.
//
// Pass null to intentionally skip reporting — mirrors ErrorPage's appBrokenSource
// prop, for strip-banner types that aren't actual app breakage (see StripErrorBanner).
const useReportAppBrokenBanner = (source: AppBrokenSource | null, errorType: ErrorBannerValue, route?: string) => {
  // useWatch (not useSideEffectOnMount) because the banner type can change without a
  // remount — it reports on first render and again on each type change, but not on
  // plain re-renders.
  useWatch(errorType, (newType) => {
    if (source === null) return;
    captureAppBrokenMessage(source, newType, route);
  });
};

// Strip-banner types that are NOT actual app breakage, so they're excluded from
// app_broken for consistency with ErrorPage's HTTP-level exclusions:
// - homeIslandsLoggedOut mirrors the excluded loggedOut/401 path
// - requestBlocked mirrors ErrorPage's excluded request-blocked HTTP type
// - studentsNotInClass is a user-input validation error, not app breakage
// - badRequest is a 400 from a bad URL the user entered, not app breakage
const NON_BREAKAGE_STRIP_TYPES: ReadonlySet<ErrorBannerValue> = new Set([
  ErrorBannerValues.homeIslandsLoggedOut,
  ErrorBannerValues.requestBlocked,
  ErrorBannerValues.studentsNotInClass,
  ErrorBannerValues.badRequest,
]);

const RealtimeSyncBanner = ({ onClose }: { onClose?: () => void }) => {
  useReportAppBrokenBanner("network_fallback", ErrorBannerValues.realtimeSync);

  // aria-live lives on the wrapper because DDSScreenBanner's props don't expose role/aria-*;
  // since DDSScreenBanner has no live-region of its own, this does not double-announce.
  return (
    <div
      className={styles.ddsBannerContainer}
      data-promo-id="realtime_sync_banner"
      role="alert"
      aria-live="polite"
      aria-label={t`Sync error banner`}
    >
      <DDSScreenBanner data-name="error-banner.realtime-sync" variant="warning" onClose={onClose}>
        <DDSBodyText level="md" textAlign="center">
          {t`We're having difficulty syncing. Please refresh the page.`}
          <br />
          <Trans>
            If the problem persists, please view our{" "}
            <DDSExternalLink
              href="https://help.classdojo.com/hc/en-us/articles/202793985-Sync-Errors-and-Issues"
              data-name="error-banner.realtime-sync.help-link"
              className={styles.ddsBannerHelpLink}
            >
              help center documentation
            </DDSExternalLink>
            .
          </Trans>
        </DDSBodyText>
      </DDSScreenBanner>
    </div>
  );
};

const StripErrorBanner = ({
  errorType,
  href,
  alreadyReported,
  route,
}: {
  errorType: ErrorBannerValue;
  href: string;
  alreadyReported?: boolean;
  route?: string;
}): JSX.Element => {
  // alreadyReported: the incident was captured as an exception carrying the app_broken
  // tags (unhandledAppBrokenHint), so reporting again here would double-count it.
  useReportAppBrokenBanner(
    alreadyReported || NON_BREAKAGE_STRIP_TYPES.has(errorType) ? null : "error_banner",
    errorType,
    route,
  );

  // Every branch renders into the same unkeyed <span className={styles.message}>, so switching
  // error type reconciles that span in place. Each copy run is therefore a bare string inside its
  // own element — `t` rather than `<Trans>`, which would compile to an element child.
  // See @web-monorepo/translation-safe-dom
  let contents;
  switch (errorType) {
    // badRequest shares the unknown copy verbatim; only its app_broken reporting differs.
    case ErrorBannerValues.unknown:
    case ErrorBannerValues.badRequest:
    case ErrorBannerValues.homeIslands:
    case ErrorBannerValues.moduleLoadError:
      contents = (
        <span className={styles.message}>
          <TranslationSafeText>
            {errorType === ErrorBannerValues.moduleLoadError
              ? t`We're having trouble loading. Please `
              : t`Whoops! Looks like something went wrong. Please `}
          </TranslationSafeText>
          <GoHome href={href} />
          <TranslationSafeText>{t`.`}</TranslationSafeText>
        </span>
      );
      break;
    case ErrorBannerValues.homeIslandsLoggedOut:
      contents = (
        <span className={styles.message}>
          <TranslationSafeText>{t`Oops, something went wrong, please try again later`}</TranslationSafeText>
        </span>
      );
      break;
    case ErrorBannerValues.requestBlocked:
      contents = (
        <span className={styles.message}>
          <TranslationSafeText>
            {t`Oh no! ClassDojo can't be accessed on this device. Check with your admin to help unblock it.`}
          </TranslationSafeText>
        </span>
      );
      break;
    case ErrorBannerValues.studentsNotInClass:
      contents = (
        <span className={styles.message}>
          <TranslationSafeText>
            {t`You tried to give points to one or more students that are not in this class. `}
          </TranslationSafeText>
          <GoHome href={window.location.href} />
        </span>
      );
      break;
  }

  return (
    <div
      data-name="globalErrorBanner"
      className={styles.container}
      role="alert"
      aria-live="polite"
      aria-label={t`Error banner`}
    >
      {contents}
    </div>
  );
};

export const ErrorBanner = ({
  errorType,
  href = "/",
  onClose,
  alreadyReported,
  route,
}: ErrorBannerProps): JSX.Element => {
  // The banner can mount outside the app's locale provider while catalogs are still loading.
  useI18nLocale();
  React.useEffect(() => {
    if (Config.nodeEnv === "test" && !global.errorBannerAllowed) {
      const timeout = setTimeout(() => {
        throw new Error(`Should not show error banner in tests. Error type was: ${errorType}`);
      }, 0);
      return () => clearTimeout(timeout);
    }
  }, [errorType]);

  switch (errorType) {
    case ErrorBannerValues.rateLimit:
      return <ErrorPage reloadButtonText={t`Try again`} errorType="429" />;
    case ErrorBannerValues.forbidden:
      return <ErrorPage noReloadButton errorType="403" />;
    case ErrorBannerValues.apiDown:
      return <ErrorPage reloadButtonText={t`Try again`} errorType="500" />;
    case ErrorBannerValues.loggedOut:
      return <ErrorPage reloadButtonText={t`Click here to sign in again`} reloadButtonHref={href} errorType="401" />;
    case ErrorBannerValues.apiDownLoadShedding:
      return (
        <ErrorPage
          reloadButtonText={t`Check our status`}
          errorType="500"
          reloadButtonHref={"https://status.classdojo.com"}
        >
          <DDSBodyText>
            {t`We're so sorry - currently ClassDojo is seeing higher than normal usage. We're working hard to resolve this.`}
          </DDSBodyText>
        </ErrorPage>
      );
    case ErrorBannerValues.notFound:
      return <ErrorPage reloadButtonText={t`Try again`} errorType="404" />;
    case ErrorBannerValues.noNetwork: {
      // Create a minimal error object that will be detected as NoNetworkError by ErrorPage
      const networkError = new Error("Network error - no internet connection");
      networkError.name = "NoNetworkError";
      return <ErrorPage error={networkError} reloadButtonText={t`Try again`} appBrokenSource="network_fallback" />;
    }
    case ErrorBannerValues.realtimeSync:
      return <RealtimeSyncBanner onClose={onClose} />;
    default:
      return <StripErrorBanner errorType={errorType} href={href} alreadyReported={alreadyReported} route={route} />;
  }
};

export function ErrorBannerContainer({
  initialUrl,
  renderRealtimeSyncBanner = true,
}: {
  initialUrl?: string;
  renderRealtimeSyncBanner?: boolean;
}) {
  const [bannerProps, setBannerProps] = React.useState<ErrorBannerStateProps>({
    type: null,
    refreshUrl: initialUrl,
  });

  React.useEffect(() => {
    // setBannerProps is a stable useState setter, so this registers exactly once on mount.
    return registerErrorBannerSetter(setBannerProps);
  }, []);

  if (!bannerProps.type) return null;

  // Suppressed surfaces render null but keep the state, so the banner reappears
  // (without re-triggering) as soon as the caller navigates to a surface that allows it.
  if (bannerProps.type === ErrorBannerValues.realtimeSync && !renderRealtimeSyncBanner) {
    return null;
  }

  return (
    <ErrorBanner
      errorType={bannerProps.type}
      href={bannerProps.refreshUrl}
      alreadyReported={bannerProps.alreadyReported}
      route={bannerProps.route}
      onClose={() => setBannerProps((prev) => ({ ...prev, type: null }))}
    />
  );
}
